import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
    FacebookAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    getAuth,
    signInWithPopup,
    signOut,
} from 'firebase/auth';

// utils
import { firebaseApp } from '../config';
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';


// const firebaseApp = initializeApp(FIREBASE_API);

// const AUTH = getAuth(firebaseApp);

// AUTH.settings.appVerificationDisabledForTesting  = true; 
// ----------------------------------------------------------------------

const initialState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
};

const handlers = {
    INITIALIZE: (state, action) => {
        const { isAuthenticated, user } = action.payload;
        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            user,
        };
    },

    OTPFINAL: (state, action) => {
        const { final } = action.payload;
        return {
            ...state,
            isAuthenticated: false,
            isInitialized: true,
            final,
            user: null,
        }
    },
    LOGINED: (state, action) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user,
        };
    },
    LOGOUT: (state) => ({
        ...state,
        isAuthenticated: false,
        final: null,

        user: null,
    }),


};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
    ...initialState,
    method: 'jwt',
    login: () => Promise.resolve(),
    socialLogin: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    initialize: () => Promise.resolve(),
});

const firebaseAuth = getAuth(firebaseApp);

const createSocialProvider = (providerName) => {
    switch (providerName) {
        case 'google': {
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');
            return provider;
        }
        case 'facebook': {
            const provider = new FacebookAuthProvider();
            provider.addScope('email');
            return provider;
        }
        case 'apple': {
            const provider = new OAuthProvider('apple.com');
            provider.addScope('email');
            provider.addScope('name');
            return provider;
        }
        default:
            throw new Error('Unsupported social login provider');
    }
};

const formatSocialLoginError = (error) => {
    switch (error?.code) {
        case 'auth/popup-closed-by-user':
            return 'Social login popup was closed before completion';
        case 'auth/cancelled-popup-request':
            return 'Social login was cancelled';
        case 'auth/popup-blocked':
            return 'Popup was blocked by the browser. Please allow popups and try again';
        default:
            return error?.response?.data?.message || error?.message || 'Social login failed';
    }
};

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
    children: PropTypes.node,
};

function AuthProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const initialize = async () => {
        try {
            const accessToken = window.localStorage.getItem('accessToken');
            if (accessToken && isValidToken(accessToken)) {
                setSession(accessToken);
                const response = await axios.get('/api/auth/my-account');
                const { user } = response.data;
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: true,
                        user,
                    },
                });
            } else {
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            }
        } catch (err) {
            console.error(err);
            dispatch({
                type: 'INITIALIZE',
                payload: {
                    isAuthenticated: false,
                    user: null,
                },
            });
        }
    };

    useEffect(() => {
        console.log("--------------iniitalize passport-------------------");
        initialize();
    }, []);

    const startVerifyMn = async (phoneNumber) => {
        const { data } = await axios.post('/api/auth/verify-mn/start', { phoneNumber });
        return data;
    }

    const pollVerifyMn = async (sessionId) => {
        const { data } = await axios.get(`/api/auth/verify-mn/status/${sessionId}`);
        if (data?.token && data?.user) {
            setSession(data.token);
            dispatch({ type: 'LOGINED', payload: { user: data.user } });
        }
        return data;
    }

    const codeVerify = async (phoneNumber, pinCode) => {
        try {
            const response = await axios.post('/api/auth/pincode', { phoneNumber, pinCode });

            // Check if 2FA is required
            if (response.data.requiresTwoFactor) {
                return {
                    success: response.data.success,
                    requiresTwoFactor: true,
                    message: response.data.message,
                    phoneNumber: response.data.phoneNumber
                };
            }

            const { token, user } = response.data;

            // verify phoneNumber via firebase
            if (!token) {
                try {
                    window.phoneNumber = phoneNumber;
                    return { success: false, message: 'pin code verification error' };
                } catch (err) {
                    return { success: false, message: 'pin code verification error' };
                }
            }
            if (user.status === "inactive") {
                return { success: false, message: 'Your account is inactive. Please contact with administrator' };
            }
            setSession(token);
            dispatch({
                type: 'LOGINED',
                payload: {
                    user,
                },
            });
            return { success: true, message: 'verification successfully' };

        } catch (error) {
            console.error('Pin code verification error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Pin code verification error'
            };
        }
    }

    const login = async (phoneNumber) => {

        const response = await axios.post('/api/auth/login', {
            phoneNumber
        });

        const isPinCode = response.data.pinVerify

        if (isPinCode) {
            return 'pincode'
        }
        const { token, user } = response.data;

        // verify phoneNumber via sms
        if (!token) {
            try {
                window.phoneNumber = phoneNumber;
                return 'otp';
            } catch (err) {
                console.log(err);
                return 'otp';
            }

        } else {

            if (user.status === "inactive") {
                return "inactive";
            }
            setSession(token);
            dispatch({
                type: 'LOGINED',
                payload: {
                    user,
                },
            });
            return 'navigate';

        }

    };

    const socialLogin = async (providerName) => {
        try {
            const provider = createSocialProvider(providerName);
            const popupResult = await signInWithPopup(firebaseAuth, provider);
            const firebaseToken = await popupResult.user.getIdToken(true);
            const response = await axios.post('/api/auth/social-login', {
                firebaseToken,
                provider: providerName,
            });

            if (response.data.requiresTwoFactor) {
                return {
                    success: true,
                    requiresTwoFactor: true,
                    phoneNumber: response.data.phoneNumber,
                    message: response.data.message,
                };
            }

            const { token, user } = response.data;
            if (!token || !user) {
                return {
                    success: false,
                    message: 'Social login failed',
                };
            }

            if (user.status === 'inactive') {
                return {
                    success: false,
                    message: 'Your account is inactive. Please contact with administrator',
                };
            }

            setSession(token);
            dispatch({
                type: 'LOGINED',
                payload: {
                    user,
                },
            });

            return { success: true, requiresTwoFactor: false };
        } catch (error) {
            console.error('Social login error:', error);
            return {
                success: false,
                message: formatSocialLoginError(error),
            };
        } finally {
            try {
                await signOut(firebaseAuth);
            } catch (signOutError) {
                console.warn('Firebase social auth sign-out failed:', signOutError);
            }
        }
    };


    const verify2FA = async (phoneNumber, token, isBackupCode = false) => {
        try {
            const response = await axios.post('/api/2fa/verify', {
                phoneNumber,
                token,
                isBackupCode
            });

            console.log('2FA verify response:', response.data); // Debug log

            if (response.data.status === 200) {
                // Complete login after 2FA verification
                const loginResponse = await axios.post('/api/auth/complete-login-2fa', {
                    phoneNumber
                });

                console.log('Complete login response:', loginResponse.data); // Debug log

                if (loginResponse.data.success) {
                    const { token, user } = loginResponse.data;
                    setSession(token);
                    dispatch({
                        type: 'LOGINED',
                        payload: {
                            user,
                        },
                    });
                    return { success: true };
                }
            }

            return { success: false, message: response.data.message || 'Verification failed' };
        } catch (error) {
            console.error('2FA verification error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Verification failed'
            };
        }
    };

    const logout = async () => {
        try {
            setSession(null);

            dispatch({ type: 'LOGOUT' });
            // signOut(AUTH);
        } catch (err) {

            console.log(err);
        }

    };

    return (<AuthContext.Provider value={
        {
            ...state,
            method: 'jwt',
            login,
            socialLogin,
            logout,
            startVerifyMn,
            pollVerifyMn,
            codeVerify,
            verify2FA,
            initialize,
        }
    } > {children} </AuthContext.Provider>);
}

export { AuthContext, AuthProvider };